import { memo } from 'react';

import dayjs from 'dayjs';

import { mapTeamMemberStatusFromDTO } from '@/modules/common/mappers/team-member-status.mapper';
import { MemberStatus } from '@/modules/team';

import { getStateChangeForDate } from '../../helpers';
import usePlannerStore from '../../store';
import { RowItem } from '../../types';
import { PlannerUserStateChangeResponse } from '../../types/dto';
import { PlannerTeamMemberInfo } from '../planner-team-member-info';

interface TimelineRowSidebarProps {
  row: RowItem;
  userStartDate: dayjs.Dayjs | undefined;
}

/**
 * Sidebar component for timeline rows that displays user information.
 * Subscribes to currentStateChange only when mounted (phase 2 of deferred rendering).
 */
export const TimelineRowSidebar = memo(({ row, userStartDate }: TimelineRowSidebarProps) => {
  const currentStateChange = usePlannerStore(
    store =>
      getStateChangeForDate(
        row.stateChanges,
        store.firstVisibleDate
      ) as PlannerUserStateChangeResponse | null
  );

  const currentWorkingMinutesPerDay = currentStateChange?.workingMinutesPerDay
    ? currentStateChange.workingMinutesPerDay
    : 0;
  const currentBillableCapacityPercent = Math.round(
    currentStateChange?.billableCapacityPercent ?? 0
  );
  const currentDepartment = currentStateChange?.department?.name;
  const currentRole = currentStateChange?.role?.name;
  const currentSeniority = currentStateChange?.seniority?.name;
  const currentCountry = currentStateChange?.country ?? null;
  const currentCity = currentStateChange?.city ?? null;
  const currentIsManager = currentStateChange?.isManager ?? false;
  const currentMemberManagerName = currentStateChange?.manager?.name ?? null;
  const currentMembershipType = currentStateChange?.membershipType ?? null;
  const currentStatus = currentStateChange?.displayStatus
    ? mapTeamMemberStatusFromDTO(currentStateChange.displayStatus)
    : MemberStatus.Basic;
  const currentOnDemand = currentStateChange?.onDemand;

  return (
    <PlannerTeamMemberInfo
      title={row.title}
      status={currentStatus}
      onDemand={currentOnDemand}
      avatarLink={row.user?.profileImageUrl ?? null}
      userStartDate={userStartDate}
      archivedDate={
        currentStatus === MemberStatus.Archived && currentStateChange?.effectiveStartDate
          ? dayjs(currentStateChange.effectiveStartDate)
          : undefined
      }
      details={{
        billableCapacityPercent: currentBillableCapacityPercent,
        workingMinutesPerDay: currentWorkingMinutesPerDay,
        isManager: currentIsManager,
        memberManagerName: currentMemberManagerName,
        department: currentDepartment,
        role: currentRole,
        seniority: currentSeniority,
        country: currentCountry,
        city: currentCity,
        membershipType: currentMembershipType,
        customAttributes: row.customAttributes,
      }}
    />
  );
});
